import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { PriceListItem, PriceListClient } from '../services/api';
import { Product, KameoneFicha } from '../types/api';
import { Loader } from './Loader';
import { Button } from './Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './Tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './Dialog';
import { ArrowLeft, DownloadSimple, MagnifyingGlass, Plus } from 'phosphor-react';
import { DeleteButton } from './DeleteButton';

interface PriceListDetailProps {
  listId: number;
  onBack: () => void;
  onSaved?: () => void;
}

const PAGE_SIZE = 50;

export const PriceListDetail: React.FC<PriceListDetailProps> = ({ listId, onBack, onSaved }) => {
  const api = useApi();
  const [listName, setListName] = useState('');
  const [activeTab, setActiveTab] = useState('items');

  // Items state
  const [items, setItems] = useState<PriceListItem[]>([]);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [itemsOffset, setItemsOffset] = useState(0);
  const [itemsSearch, setItemsSearch] = useState('');
  const [itemsSearchInput, setItemsSearchInput] = useState('');
  const [loadingItems, setLoadingItems] = useState(true);

  // Clients state
  const [clients, setClients] = useState<PriceListClient[]>([]);
  const [loadingClients, setLoadingClients] = useState(true);

  // Inline price editing
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editingPrice, setEditingPrice] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);

  // Add items dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState<Product[]>([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Map<number, { product: Product; price: string }>>(new Map());
  const [addingItems, setAddingItems] = useState(false);

  // Add clients dialog
  const [addClientDialogOpen, setAddClientDialogOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [fichaResults, setFichaResults] = useState<KameoneFicha[]>([]);
  const [searchingFichas, setSearchingFichas] = useState(false);
  const [addingClient, setAddingClient] = useState<string | null>(null);

  // Client filter
  const [clientsFilterInput, setClientsFilterInput] = useState('');

  // Export state
  const [exporting, setExporting] = useState(false);

  // General error
  const [error, setError] = useState<string | null>(null);

  // Load list name
  useEffect(() => {
    api.getPriceListById(listId).then(detail => {
      setListName(detail.name);
    }).catch(() => {});
  }, [listId, api]);

  // Load items
  const loadItems = useCallback(async () => {
    try {
      setLoadingItems(true);
      const result = await api.getPriceListItems(listId, PAGE_SIZE, itemsOffset, itemsSearch || undefined);
      setItems(result.items);
      setItemsTotal(result.pagination.total);
    } catch {
      setError('No se pudieron cargar los items.');
    } finally {
      setLoadingItems(false);
    }
  }, [listId, itemsOffset, itemsSearch, api]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // Load clients
  const loadClients = useCallback(async () => {
    try {
      setLoadingClients(true);
      const data = await api.getPriceListClients(listId);
      setClients(data);
    } catch {
      // silent
    } finally {
      setLoadingClients(false);
    }
  }, [listId, api]);

  useEffect(() => { loadClients(); }, [loadClients]);

  // Search items
  const handleItemsSearch = () => {
    setItemsOffset(0);
    setItemsSearch(itemsSearchInput);
  };

  // Pagination
  const totalPages = Math.ceil(itemsTotal / PAGE_SIZE);
  const currentPage = Math.floor(itemsOffset / PAGE_SIZE) + 1;

  // Inline price editing
  const startEditPrice = (item: PriceListItem) => {
    setEditingItemId(item.id);
    setEditingPrice(item.price.toString());
  };

  const savePrice = async () => {
    if (editingItemId === null) return;
    const price = parseFloat(editingPrice);
    if (isNaN(price) || price <= 0) { setEditingItemId(null); return; }
    try {
      setSavingPrice(true);
      await api.updatePriceListItem(listId, editingItemId, price);
      setItems(prev => prev.map(it => it.id === editingItemId ? { ...it, price } : it));
      onSaved?.();
    } catch {
      // revert
    } finally {
      setSavingPrice(false);
      setEditingItemId(null);
    }
  };

  const cancelEditPrice = () => {
    setEditingItemId(null);
  };

  // Delete item
  const handleDeleteItem = async (itemId: number) => {
    try {
      await api.deletePriceListItem(listId, itemId);
      setItems(prev => prev.filter(it => it.id !== itemId));
      setItemsTotal(prev => prev - 1);
      onSaved?.();
    } catch {
      // silent
    }
  };

  // Delete client
  const handleDeleteClient = async (clientId: number) => {
    try {
      await api.deletePriceListClient(listId, clientId);
      setClients(prev => prev.filter(c => c.id !== clientId));
      onSaved?.();
    } catch {
      // silent
    }
  };

  // Add client dialog: search fichas with debounce
  useEffect(() => {
    if (!addClientDialogOpen) return;
    if (!clientSearch.trim()) { setFichaResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        setSearchingFichas(true);
        const isRut = /^[\d.-]+$/.test(clientSearch.trim());
        const results = isRut
          ? await api.getKameoneFichas(clientSearch.trim())
          : await api.getKameoneFichas(undefined, clientSearch.trim());
        setFichaResults(results || []);
      } catch {
        setFichaResults([]);
      } finally {
        setSearchingFichas(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch, addClientDialogOpen, api]);

  const handleAddClient = async (ficha: KameoneFicha) => {
    const razonSocial = ficha.RazonSocial?.trim();
    if (!razonSocial || !listName) return;
    // Check if already assigned
    if (clients.some(c => c.razon_social.toLowerCase() === razonSocial.toLowerCase())) return;
    try {
      setAddingClient(razonSocial);
      await api.assignClientToPriceList(razonSocial, listName);
      await loadClients();
      onSaved?.();
    } catch {
      // silent
    } finally {
      setAddingClient(null);
    }
  };

  const openAddClientDialog = () => {
    setClientSearch('');
    setFichaResults([]);
    setAddClientDialogOpen(true);
  };

  // Filtered clients for display
  const filteredClients = clientsFilterInput.trim()
    ? clients.filter(c => c.razon_social.toLowerCase().includes(clientsFilterInput.toLowerCase()))
    : clients;

  // Export CSV
  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await api.exportPriceListCsv(listId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${listName || 'lista'}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // silent
    } finally {
      setExporting(false);
    }
  };

  // Add items dialog: search products
  const searchProducts = useCallback(async (query: string) => {
    if (!query.trim()) { setProductResults([]); return; }
    try {
      setSearchingProducts(true);
      const result = await api.getProducts(query, 20);
      setProductResults(result.data || []);
    } catch {
      setProductResults([]);
    } finally {
      setSearchingProducts(false);
    }
  }, [api]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearch.trim()) searchProducts(productSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch, searchProducts]);

  const toggleProduct = (product: Product) => {
    setSelectedProducts(prev => {
      const next = new Map(prev);
      if (next.has(product.id)) {
        next.delete(product.id);
      } else {
        next.set(product.id, { product, price: product.price?.toString() || '' });
      }
      return next;
    });
  };

  const updateSelectedPrice = (productId: number, price: string) => {
    setSelectedProducts(prev => {
      const next = new Map(prev);
      const entry = next.get(productId);
      if (entry) next.set(productId, { ...entry, price });
      return next;
    });
  };

  const handleAddItems = async () => {
    const itemsToAdd: { productId: number; price: number }[] = [];
    Array.from(selectedProducts.entries()).forEach(([productId, entry]) => {
      const price = parseFloat(entry.price);
      if (isNaN(price) || price <= 0) return;
      itemsToAdd.push({ productId, price });
    });
    if (itemsToAdd.length === 0) return;
    try {
      setAddingItems(true);
      await api.addPriceListItems(listId, itemsToAdd);
      setAddDialogOpen(false);
      setSelectedProducts(new Map());
      setProductSearch('');
      setProductResults([]);
      loadItems();
      onSaved?.();
    } catch {
      // silent
    } finally {
      setAddingItems(false);
    }
  };

  const openAddDialog = () => {
    setSelectedProducts(new Map());
    setProductSearch('');
    setProductResults([]);
    setAddDialogOpen(true);
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="p-1.5 rounded hover:bg-muted text-text-subtle hover:text-text transition-colors"
            aria-label="Volver"
          >
            <ArrowLeft size={20} weight="bold" />
          </button>
          <h2 className="font-sans font-semibold text-xl text-foreground">
            {listName || 'Cargando...'}
          </h2>
        </div>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<DownloadSimple size={16} weight="bold" />}
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? 'Exportando...' : 'Exportar CSV'}
        </Button>
      </div>

      {error && (
        <p className="font-sans text-sm text-destructive">{error}</p>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="border-b border-border mb-6">
          <TabsTrigger value="items" badgeCount={itemsTotal}>Items</TabsTrigger>
          <TabsTrigger value="clients" badgeCount={clients.length}>Clientes</TabsTrigger>
        </TabsList>

        {/* Items Tab */}
        <TabsContent value="items">
          <div className="space-y-4">
            {/* Search + Add */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle" />
                <input
                  type="text"
                  placeholder="Buscar por nombre o SKU..."
                  value={itemsSearchInput}
                  onChange={(e) => setItemsSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleItemsSearch(); }}
                  className="w-full pl-9 pr-4 py-2 rounded-md border border-border bg-background text-sm font-sans outline-none focus:border-primary transition-colors"
                />
              </div>
              <Button variant="ghost" size="sm" onClick={handleItemsSearch}>
                Buscar
              </Button>
              <Button variant="primary" size="sm" leftIcon={<Plus size={16} weight="bold" />} onClick={openAddDialog}>
                Agregar items
              </Button>
            </div>

            {loadingItems ? (
              <div className="flex items-center gap-2 text-text-subtle py-8 justify-center">
                <Loader />
                <span className="font-sans text-sm">Cargando items...</span>
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 border border-dashed border-border rounded-lg">
                <p className="font-sans text-sm text-text-subtle text-center">
                  {itemsSearch ? 'No se encontraron items.' : 'Esta lista no tiene items a\u00fan.'}
                </p>
              </div>
            ) : (
              <>
                {/* Table */}
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full text-sm font-sans">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left px-4 py-2 font-medium text-text-subtle">Producto</th>
                        <th className="text-left px-4 py-2 font-medium text-text-subtle">SKU</th>
                        <th className="text-right px-4 py-2 font-medium text-text-subtle">Precio</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={item.id} className="border-b border-border last:border-b-0 hover:bg-muted/20">
                          <td className="px-4 py-2.5 text-text">{item.product_name}</td>
                          <td className="px-4 py-2.5 text-text-subtle">{item.sku}</td>
                          <td className="px-4 py-2.5 text-right">
                            {editingItemId === item.id ? (
                              <input
                                type="number"
                                value={editingPrice}
                                onChange={(e) => setEditingPrice(e.target.value)}
                                onBlur={savePrice}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') savePrice();
                                  if (e.key === 'Escape') cancelEditPrice();
                                }}
                                disabled={savingPrice}
                                autoFocus
                                className="w-24 px-2 py-1 rounded border border-primary bg-background text-sm text-right outline-none"
                              />
                            ) : (
                              <button
                                type="button"
                                onClick={() => startEditPrice(item)}
                                className="text-text hover:text-primary hover:underline cursor-pointer transition-colors"
                              >
                                {formatPrice(item.price)}
                              </button>
                            )}
                          </td>
                          <td className="px-2 py-2.5">
                            <DeleteButton onDelete={() => handleDeleteItem(item.id)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between text-sm text-text-subtle">
                    <span>
                      {itemsOffset + 1}–{Math.min(itemsOffset + PAGE_SIZE, itemsTotal)} de {itemsTotal}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setItemsOffset(prev => Math.max(0, prev - PAGE_SIZE))}
                      >
                        Anterior
                      </Button>
                      <span className="text-text">
                        {currentPage} / {totalPages}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={currentPage >= totalPages}
                        onClick={() => setItemsOffset(prev => prev + PAGE_SIZE)}
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>

        {/* Clients Tab */}
        <TabsContent value="clients">
          <div className="space-y-4">
            {/* Search + Add */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle" />
                <input
                  type="text"
                  placeholder="Filtrar clientes asignados..."
                  value={clientsFilterInput}
                  onChange={(e) => setClientsFilterInput(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 rounded-md border border-border bg-background text-sm font-sans outline-none focus:border-primary transition-colors"
                />
              </div>
              <Button variant="primary" size="sm" leftIcon={<Plus size={16} weight="bold" />} onClick={openAddClientDialog}>
                Agregar cliente
              </Button>
            </div>

            {loadingClients ? (
              <div className="flex items-center gap-2 text-text-subtle py-8 justify-center">
                <Loader />
                <span className="font-sans text-sm">Cargando clientes...</span>
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 border border-dashed border-border rounded-lg">
                <p className="font-sans text-sm text-text-subtle text-center">
                  {clientsFilterInput.trim()
                    ? 'No se encontraron clientes.'
                    : 'No hay clientes asignados a esta lista.'}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {filteredClients.map((client) => (
                  <div
                    key={client.id}
                    className="flex items-center justify-between px-4 py-3 rounded-lg border border-border bg-background"
                  >
                    <p className="font-sans font-medium text-sm text-text">{client.razon_social}</p>
                    <DeleteButton onDelete={() => handleDeleteClient(client.id)} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Items Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="p-6 max-w-lg">
          <DialogHeader className="mb-4">
            <DialogTitle>Agregar items a la lista</DialogTitle>
          </DialogHeader>

          {/* Product search */}
          <div className="relative mb-4">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle" />
            <input
              type="text"
              placeholder="Buscar producto por nombre o SKU..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              autoFocus
              className="w-full pl-9 pr-4 py-2 rounded-md border border-border bg-background text-sm font-sans outline-none focus:border-primary transition-colors"
            />
          </div>

          {/* Search results */}
          <div className="max-h-48 overflow-y-auto border border-border rounded-lg mb-4">
            {searchingProducts ? (
              <div className="flex items-center gap-2 text-text-subtle py-4 justify-center">
                <Loader />
                <span className="text-sm">Buscando...</span>
              </div>
            ) : productResults.length === 0 ? (
              <p className="text-sm text-text-subtle text-center py-4">
                {productSearch.trim() ? 'Sin resultados.' : 'Escribe para buscar productos.'}
              </p>
            ) : (
              productResults.map((p) => {
                const isSelected = selectedProducts.has(p.id);
                return (
                  <div
                    key={p.id}
                    className={`flex items-center justify-between px-3 py-2 cursor-pointer border-b border-border last:border-b-0 transition-colors ${isSelected ? 'bg-primary/5' : 'hover:bg-muted/50'}`}
                    onClick={() => toggleProduct(p)}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-text truncate">{p.name}</p>
                      <p className="text-xs text-text-subtle">{p.sku}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      {isSelected && (
                        <input
                          type="number"
                          placeholder="Precio"
                          value={selectedProducts.get(p.id)?.price || ''}
                          onChange={(e) => { e.stopPropagation(); updateSelectedPrice(p.id, e.target.value); }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-24 px-2 py-1 rounded border border-border bg-background text-sm text-right outline-none focus:border-primary"
                        />
                      )}
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-primary border-primary' : 'border-border'}`}>
                        {isSelected && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Selected count */}
          {selectedProducts.size > 0 && (
            <p className="text-sm text-text-subtle mb-2">
              {selectedProducts.size} producto{selectedProducts.size !== 1 ? 's' : ''} seleccionado{selectedProducts.size !== 1 ? 's' : ''}
            </p>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddDialogOpen(false)} disabled={addingItems}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleAddItems}
              disabled={addingItems || selectedProducts.size === 0}
            >
              {addingItems ? <Loader /> : 'Agregar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Client Dialog */}
      <Dialog open={addClientDialogOpen} onOpenChange={setAddClientDialogOpen}>
        <DialogContent className="p-6 max-w-lg">
          <DialogHeader className="mb-4">
            <DialogTitle>Agregar cliente a la lista</DialogTitle>
          </DialogHeader>

          {/* Ficha search */}
          <div className="relative mb-4">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle" />
            <input
              type="text"
              placeholder="Buscar por RUT o raz&oacute;n social..."
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              autoFocus
              className="w-full pl-9 pr-4 py-2 rounded-md border border-border bg-background text-sm font-sans outline-none focus:border-primary transition-colors"
            />
          </div>

          {/* Search results */}
          <div className="max-h-64 overflow-y-auto border border-border rounded-lg mb-4">
            {searchingFichas ? (
              <div className="flex items-center gap-2 text-text-subtle py-4 justify-center">
                <Loader />
                <span className="text-sm">Buscando...</span>
              </div>
            ) : fichaResults.length === 0 ? (
              <p className="text-sm text-text-subtle text-center py-4">
                {clientSearch.trim() ? 'Sin resultados.' : 'Escribe un RUT o nombre para buscar.'}
              </p>
            ) : (
              fichaResults.map((ficha, idx) => {
                const razon = ficha.RazonSocial?.trim() || '';
                const alreadyAssigned = clients.some(
                  c => c.razon_social.toLowerCase() === razon.toLowerCase()
                );
                const isAdding = addingClient === razon;
                return (
                  <div
                    key={`${ficha.Rut || idx}`}
                    className={`flex items-center justify-between px-3 py-2.5 border-b border-border last:border-b-0 ${alreadyAssigned ? 'opacity-50' : ''}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-text truncate">{razon || '—'}</p>
                      <p className="text-xs text-text-subtle">
                        {ficha.Rut || 'Sin RUT'}
                        {ficha.Comuna ? ` · ${ficha.Comuna}` : ''}
                      </p>
                    </div>
                    <div className="ml-2">
                      {alreadyAssigned ? (
                        <span className="text-xs text-text-subtle font-medium">Ya asignado</span>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAddClient(ficha)}
                          disabled={isAdding || !razon}
                        >
                          {isAdding ? <Loader /> : 'Agregar'}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddClientDialogOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
